import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserPortfolio } from '../../../shared/services/apiGateway';
import isTransactionComplete from '../../../shared/helpers/isTransactionComplete';
import {
  getSyndicationProgramSignUpCompletedDate,
  isUserEligibleToInvestInSyndication,
  isWithinSyndicationProgramWaitingPeriod,
} from '../../../shared/helpers/syndicationProgramHelpers';

const useSyndicationAccess = () => {
  const user = useSelector((state) => state.auth.user);
  const authType = useSelector((state) => state.auth.authType);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const [userPortfolio, setUserPortfolio] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedPortfolio, setHasLoadedPortfolio] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!user || authType === 'MANAGED') {
      setUserPortfolio({});
      setIsLoading(false);
      setHasLoadedPortfolio(false);
      setIsError(false);
      return undefined;
    }

    const abortController = new AbortController();
    let ignore = false;

    async function fetchData() {
      setIsLoading(true);
      setHasLoadedPortfolio(false);
      setIsError(false);

      try {
        const portfolio = await getUserPortfolio(true, false, false, abortController.signal);
        if (!ignore && portfolio !== 'aborted' && portfolio.data) {
          setUserPortfolio(portfolio.data);
        }
      } catch (error) {
        if (!ignore && error.name !== 'AbortError') {
          setUserPortfolio({});
          setIsError(true);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
          setHasLoadedPortfolio(true);
        }
      }
    }

    fetchData();

    return () => {
      ignore = true;
      abortController.abort();
    };
  }, [authType, user]);

  const isInvestor = user?.isInvestor === 1;
  const syndicationProgramSignUpCompletedDate = getSyndicationProgramSignUpCompletedDate(userPortfolio);
  const withinWaitingPeriod = isWithinSyndicationProgramWaitingPeriod(syndicationProgramSignUpCompletedDate);
  const { isEligible } = isUserEligibleToInvestInSyndication(userPortfolio);
  const hasAdminBypass = !!userPortfolio?.investingWaitingPeriodBypassEnabledDate;
  const isUkPerson = Boolean(userPortfolio?.ukPersonSelfDeclarationDate);
  const hasCompletedInvestment = Array.isArray(userPortfolio?.transactions)
    && userPortfolio.transactions.some((transaction) => isTransactionComplete(transaction));
  const canViewSyndications = isInvestor
    && authType !== 'MANAGED'
    && !isUkPerson
    && (
      hasCompletedInvestment
      || (isEligible && (!withinWaitingPeriod || hasAdminBypass))
    );

  return {
    canViewSyndications,
    hasLoadedPortfolio,
    isError,
    isUkPerson,
    isLoading: isLoading || (
      isLoggedIn
      && isInvestor
      && authType !== 'MANAGED'
      && !hasLoadedPortfolio
    ),
    isLoggedIn,
    user,
    userPortfolio,
  };
};

export default useSyndicationAccess;
